import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { categoriesApi } from '@/services/categories'
import { checklistApi } from '@/services/checklist'
import type { JobChecklistTemplate, JobChecklistTemplateItem } from '@/types'

const MAX_TEMPLATE_ITEMS = 50

type TemplateItemDraft = {
  key: string
  title: string
  description: string
  scope: 'job' | 'work_day'
  is_required: boolean
  requires_signature: boolean
}

type TemplateFormState = {
  name: string
  description: string
  linkType: 'category' | 'default'
  job_category: string
  is_default: boolean
  active: boolean
  items: TemplateItemDraft[]
}

const emptyItem = (): TemplateItemDraft => ({
  key: `${Date.now()}-${Math.random()}`,
  title: '',
  description: '',
  scope: 'job',
  is_required: true,
  requires_signature: false,
})

const defaultFormState = (): TemplateFormState => ({
  name: '',
  description: '',
  linkType: 'category',
  job_category: '',
  is_default: false,
  active: true,
  items: [emptyItem()],
})

const toDraftItems = (items?: JobChecklistTemplateItem[]): TemplateItemDraft[] => {
  if (!items?.length) {
    return [emptyItem()]
  }

  return items.map((item, index) => ({
    key: String(item.id ?? `${index}-${item.title}`),
    title: item.title,
    description: item.description ?? '',
    scope: item.scope === 'work_day' ? 'work_day' : 'job',
    is_required: item.is_required !== false,
    requires_signature: item.requires_signature === true,
  }))
}

const buildPayload = (form: TemplateFormState) => {
  const items = form.items
    .map((item, index) => ({
      title: item.title.trim(),
      description: item.description.trim(),
      scope: item.scope,
      is_required: item.is_required,
      requires_signature: item.requires_signature,
      sort_order: index,
    }))
    .filter((item) => item.title.length > 0)

  return {
    name: form.name.trim(),
    description: form.description.trim(),
    job_category: form.linkType === 'default' ? null : (form.job_category || null),
    is_default: form.linkType === 'default',
    active: form.active,
    items,
  }
}

function countItemsByScope(items: JobChecklistTemplateItem[] | undefined, scope: 'job' | 'work_day') {
  return (items ?? []).filter((item) => (item.scope === 'work_day' ? 'work_day' : 'job') === scope).length
}

function countSignatureItems(items: JobChecklistTemplateItem[] | undefined) {
  return (items ?? []).filter((item) => item.requires_signature === true).length
}

type JobChecklistTemplateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: JobChecklistTemplate | null
  kind: 'job' | 'project' | 'event'
  onSaved?: () => void
}

function JobChecklistTemplateDialog({
  open,
  onOpenChange,
  template,
  kind,
  onSaved,
}: JobChecklistTemplateDialogProps) {
  const labels = useWorkspaceLabels()
  const isEditing = Boolean(template?.id)
  const [form, setForm] = useState<TemplateFormState>(defaultFormState)

  const categoriesKind = kind === 'project' ? 'project' : kind === 'event' ? 'event' : 'job'

  const categoriesQuery = useQuery({
    queryKey: ['job-categories', 'checklist-templates', kind],
    queryFn: () => categoriesApi.getCategories({ kind: categoriesKind }),
    enabled: open,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!open) {
      return
    }

    if (template) {
      setForm({
        name: template.name,
        description: template.description ?? '',
        linkType: template.is_default ? 'default' : 'category',
        job_category: template.job_category ? String(template.job_category) : '',
        is_default: Boolean(template.is_default),
        active: template.active !== false,
        items: toDraftItems(template.items),
      })
      return
    }

    setForm(defaultFormState())
  }, [open, template])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form)
      if (!payload.name) {
        throw new Error('Template name is required.')
      }
      if (payload.items.length === 0) {
        throw new Error('Add at least one checklist step.')
      }
      if (payload.items.length > MAX_TEMPLATE_ITEMS) {
        throw new Error(`A template cannot exceed ${MAX_TEMPLATE_ITEMS} steps.`)
      }
      if (payload.job_category === null && !payload.is_default) {
        throw new Error(`Select a ${labels.entityLower} category or mark this as the org default template.`)
      }
      if (payload.is_default === false && !payload.job_category) {
        throw new Error(`Select a ${labels.entityLower} category for this template.`)
      }

      if (isEditing && template?.id) {
        return checklistApi.updateTemplate(template.id, { ...payload, kind })
      }
      return checklistApi.createTemplate({ ...payload, kind })
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Checklist template updated' : 'Checklist template created')
      onOpenChange(false)
      onSaved?.()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateItem = (key: string, patch: Partial<TemplateItemDraft>) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    }))
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    setForm((current) => {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= current.items.length) {
        return current
      }
      const items = [...current.items]
      const [moved] = items.splice(index, 1)
      items.splice(nextIndex, 0, moved)
      return { ...current, items }
    })
  }

  const categoryOptions = categoriesQuery.data?.results ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit checklist template' : 'New checklist template'}</DialogTitle>
          <DialogDescription>
            {`Templates auto-apply when ${labels.entityPluralLower} and ${labels.workDayPluralLower} are created. ${labels.entity}-scoped steps apply once per ${labels.entityLower}; ${labels.workDayLower} steps repeat each visit.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="template-name">Name</Label>
              <Input
                id="template-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="e.g. Standard installation"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                rows={2}
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Optional notes for staff"
              />
            </div>
            <div className="space-y-2">
              <Label>Applies to</Label>
              <Select
                value={form.linkType}
                onValueChange={(value: 'category' | 'default') => {
                  setForm((current) => ({
                    ...current,
                    linkType: value,
                    is_default: value === 'default',
                  }))
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">{`Specific ${labels.entityLower} category`}</SelectItem>
                  <SelectItem value="default">Org default (fallback)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.linkType === 'category' ? (
              <div className="space-y-2">
                <Label>{`${labels.entity} category`}</Label>
                <Select
                  value={form.job_category || undefined}
                  onValueChange={(value) => setForm((current) => ({ ...current, job_category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-end">
                <p className="text-sm text-muted-foreground">
                  Used when no category-specific template matches.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="template-active"
              checked={form.active}
              onCheckedChange={(checked) => setForm((current) => ({ ...current, active: checked === true }))}
            />
            <Label htmlFor="template-active">Active</Label>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label>Checklist steps</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={form.items.length >= MAX_TEMPLATE_ITEMS}
                onClick={() => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }))}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add step
              </Button>
            </div>

            <div className="space-y-2">
              {form.items.map((item, index) => (
                <div key={item.key} className="rounded-lg border border-border/60 p-3">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Input
                        value={item.title}
                        onChange={(event) => updateItem(item.key, { title: event.target.value })}
                        placeholder="Step title"
                      />
                      <Input
                        value={item.description}
                        onChange={(event) => updateItem(item.key, { description: event.target.value })}
                        placeholder="Optional description"
                      />
                      <div className="flex flex-wrap items-center gap-3">
                        <Select
                          value={item.scope}
                          onValueChange={(value: 'job' | 'work_day') => updateItem(item.key, { scope: value })}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="job">{`Per ${labels.entityLower}`}</SelectItem>
                            <SelectItem value="work_day">{`Per ${labels.workDayLower}`}</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={item.is_required}
                            onCheckedChange={(checked) => updateItem(item.key, { is_required: checked === true })}
                          />
                          <span className="text-sm text-muted-foreground">Required</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={item.requires_signature}
                            onCheckedChange={(checked) => updateItem(item.key, { requires_signature: checked === true })}
                          />
                          <span className="text-sm text-muted-foreground">Signature required</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button type="button" variant="ghost" size="icon" disabled={index === 0} onClick={() => moveItem(index, -1)}>
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === form.items.length - 1}
                        onClick={() => moveItem(index, 1)}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={form.items.length <= 1}
                        onClick={() => setForm((current) => ({
                          ...current,
                          items: current.items.filter((row) => row.key !== item.key),
                        }))}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isEditing ? 'Save template' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function JobChecklistTemplatePanel({ kind = 'job' }: { kind?: 'job' | 'project' | 'event' }) {
  const queryClient = useQueryClient()
  const labels = useWorkspaceLabels()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<JobChecklistTemplate | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<JobChecklistTemplate | null>(null)

  const templatesQuery = useQuery({
    queryKey: ['job-checklist-templates', kind],
    queryFn: () => checklistApi.getTemplates({ kind }),
    staleTime: 30_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => checklistApi.deleteTemplate(id),
    onSuccess: () => {
      toast.success('Checklist template deleted')
      setTemplateToDelete(null)
      queryClient.invalidateQueries({ queryKey: ['job-checklist-templates', kind] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const templates = useMemo(
    () => templatesQuery.data?.results ?? [],
    [templatesQuery.data?.results],
  )

  const openCreateDialog = () => {
    setEditingTemplate(null)
    setDialogOpen(true)
  }

  const openEditDialog = (template: JobChecklistTemplate) => {
    setEditingTemplate(template)
    setDialogOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Checklist templates</p>
          <p className="text-xs text-muted-foreground">
            {`Manage reusable SOPs by ${labels.entityLower} category or org default. Existing ${labels.entityPluralLower} keep their own checklist copies.`}
          </p>
        </div>
        <Button size="sm" onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          New template
        </Button>
      </div>

      {templatesQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">{`No checklist templates yet. Create one to auto-apply steps on new ${labels.entityPluralLower}.`}</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Steps</TableHead>
                <TableHead>Signature required</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => {
                const jobSteps = countItemsByScope(template.items, 'job')
                const daySteps = countItemsByScope(template.items, 'work_day')
                const signatureSteps = countSignatureItems(template.items)

                return (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description ? (
                          <p className="text-xs text-muted-foreground">{template.description}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {template.is_default ? (
                        <Badge variant="secondary">Org default</Badge>
                      ) : (
                        <span>{template.job_category_name || 'Category'}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {jobSteps} {labels.entityLower} · {daySteps} daily
                    </TableCell>
                    <TableCell>
                      {signatureSteps > 0 ? (
                        <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                          {signatureSteps} step{signatureSteps === 1 ? '' : 's'}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={template.active !== false ? 'outline' : 'secondary'}>
                        {template.active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => openEditDialog(template)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setTemplateToDelete(template)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <JobChecklistTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        template={editingTemplate}
        kind={kind}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['job-checklist-templates', kind] })
        }}
      />

      <AlertDialog open={Boolean(templateToDelete)} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete checklist template</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{templateToDelete?.name}&quot;? Jobs that already copied this template will keep their checklist items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (templateToDelete?.id) {
                  deleteMutation.mutate(templateToDelete.id)
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
